/**
 * SportPicker — Select dropdown of sports with a "+ Add new sport" option.
 *
 * When the user picks "Add new sport", swaps to a text input so they can type
 * a custom sport name (e.g. tennis, climbing, hiking). The new name persists
 * automatically once the event is created (the backend's /api/sports endpoint
 * unions defaults with sports found across games/series/trips).
 *
 * Usage:
 *   <SportPicker value={form.sport} onChange={(v) => setForm({...form, sport: v})} />
 */
import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { API } from "@/App";

const CAPITALIZE = (s) => (s || "").replace(/\b\w/g, (c) => c.toUpperCase());
const ADD_SENTINEL = "__add_new__";

export const SportPicker = ({ value, onChange, testid = "sport-picker", className = "" }) => {
  const [sports, setSports] = useState(["pilates", "yoga", "gym", "strength", "boxing"]);
  const [customMode, setCustomMode] = useState(false);
  const [customValue, setCustomValue] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/sports`);
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled && Array.isArray(data.sports)) {
          setSports(data.sports);
        }
      } catch (e) { /* offline-friendly: keep defaults */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // If the current value isn't in the known list, it's a custom one — show it inline.
  const knownIncludesValue = !value || sports.includes(value);

  const handleSelect = (v) => {
    if (v === ADD_SENTINEL) {
      setCustomMode(true);
      setCustomValue("");
      return;
    }
    onChange(v);
  };

  const handleCustomConfirm = () => {
    const v = customValue.trim().toLowerCase();
    if (!v) {
      toast.error("Type a name");
      return;
    }
    if (v.length > 30) {
      toast.error("Name too long");
      return;
    }
    onChange(v);
    setSports(prev => prev.includes(v) ? prev : [...prev, v].sort());
    setCustomMode(false);
    setCustomValue("");
  };

  if (customMode) {
    return (
      <div className={`flex items-center gap-2 ${className}`} data-testid={`${testid}-custom-mode`}>
        <Input
          autoFocus
          value={customValue}
          onChange={(e) => setCustomValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); handleCustomConfirm(); }
            if (e.key === "Escape") { e.preventDefault(); setCustomMode(false); setCustomValue(""); }
          }}
          placeholder="e.g. Pilates, Boxing, Massage"
          maxLength={30}
          data-testid={`${testid}-custom-input`}
        />
        <Button type="button" size="sm" onClick={handleCustomConfirm} data-testid={`${testid}-custom-confirm`}>
          Add
        </Button>
        <Button type="button" size="icon" variant="ghost" onClick={() => { setCustomMode(false); setCustomValue(""); }} aria-label="Cancel">
          <X className="w-4 h-4" />
        </Button>
      </div>
    );
  }

  return (
    <Select value={value || ""} onValueChange={handleSelect}>
      <SelectTrigger className={className} data-testid={testid}>
        <SelectValue placeholder="Pick a type">
          {value ? CAPITALIZE(value) : ""}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        {sports.map((s) => (
          <SelectItem key={s} value={s} data-testid={`${testid}-option-${s}`}>{CAPITALIZE(s)}</SelectItem>
        ))}
        {value && !knownIncludesValue && (
          <SelectItem value={value} data-testid={`${testid}-option-current`}>{CAPITALIZE(value)} (custom)</SelectItem>
        )}
        <SelectItem value={ADD_SENTINEL} data-testid={`${testid}-add-new`}>
          <span className="inline-flex items-center gap-1.5 text-primary">
            <Plus className="w-3.5 h-3.5" /> Add a type…
          </span>
        </SelectItem>
      </SelectContent>
    </Select>
  );
};

export default SportPicker;
